/**
 * Hook for managing editor focus operations with comprehensive error handling
 * Provides robust focus management with fallback strategies
 */

import { useCallback, useRef } from "react";
import { Editor } from "@progress/kendo-react-editor";
import { handleEditorError } from "./editor-utils";
import {
  isEditorFocused as checkEditorFocus,
  getEditorView
} from "../utils/domHelpers";

// ============================================================================
// TYPES
// ============================================================================

export interface EditorFocusOptions {
  retryAttempts?: number;
  retryDelay?: number;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Custom hook for managing Kendo Editor focus operations
 * Provides reliable focus management with error handling and recovery strategies
 */
export const useEditorFocus = (
  editorRef: React.RefObject<Editor>,
  isDisabled: boolean,
  options: EditorFocusOptions = {}
) => {
  const { retryAttempts = 3, retryDelay = 100 } = options;

  const focusAttempts = useRef(0);

  /**
   * Attempts to focus the editor with multiple fallback strategies
   */
  const focusEditor = useCallback(async (): Promise<boolean> => {
    if (isDisabled) {
      return false;
    }

    const attemptFocus = async (attemptNumber: number): Promise<boolean> => {
      if (!editorRef.current) {
        throw new Error("Editor reference is null");
      }

      // Strategy 1: Use Kendo Editor's focus method
      if (
        editorRef.current.focus &&
        typeof editorRef.current.focus === "function"
      ) {
        editorRef.current.focus();
        return true;
      }

      // Strategy 2: Access ProseMirror view directly using utility
      const view = getEditorView(editorRef);
      if (view && view.focus) {
        view.focus();
        return true;
      }

      throw new Error(
        `All focus strategies failed on attempt ${attemptNumber}`
      );
    };

    for (let attempt = 1; attempt <= retryAttempts; attempt++) {
      focusAttempts.current = attempt;

      const success = await attemptFocus(attempt);
      if (success) {
        focusAttempts.current = 0;
        return true;
      }

      // If not the last attempt, wait before retrying
      if (attempt < retryAttempts) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
    }

    focusAttempts.current = 0;
    handleEditorError(
      new Error(`Failed to focus editor after ${retryAttempts} attempts`),
      "editor focus complete failure"
    );

    return false;
  }, [editorRef, isDisabled, retryAttempts, retryDelay]);

  /**
   * Checks if the editor currently has focus using utility function
   */
  const isEditorFocused = useCallback((): boolean => {
    return checkEditorFocus(editorRef);
  }, [editorRef]);

  return {
    focusEditor,
    isEditorFocused,
    focusAttempts: focusAttempts.current
  };
};
